import ProductColour from "ts/api/productColours";
import { ExtraProduct, FullProduct, ProductConfig } from "ts/api/product";

import { round } from "lodash";

import "firebase/firestore";
import Fee from "ts/api/fee";
import {
  CustomProductOptionConfigType,
  IOrganiserCompany,
  IProduct,
  IProductFileWithFile,
  IProductToExtra,
  IStage,
  SurchargeType,
} from "@evennt-tech/api-v2-core";
import { sortStages } from "view/apps/Supplier/pages/shared/SurchargeModal";
import {
  isColorItem,
  isCpoConfigQuantityAvailable,
} from "tools/custom-product-option/common";
import { getSingleProductImage } from "tools/custom-product-option/getSingleProductImage";
import { collectProductFiles } from "tools/product/collectProductFiles";
import { getDefaultCpoConfigOrFirst } from "tools/custom-product-option/getDefaultCpoEnabledValue";
import filterNotEmptyArray from "tools/common/filterNotEmptyArray";
import { parseCents } from "tools/product/formats/formatCents";
import { ICustomProductOptionConfig } from "@evennt-tech/api-v2-core/dist/custom-product-option";

export type FetchedProductColour = Record<string, ProductColour>;

const oneDayInMS = 24 * 60 * 60 * 1000;

export const calculateSurchargeAmount = (
  surchargeStages: IStage[],
  price: number,
  deliveryStart: Date
): number | SurchargeType.unpublish => {
  const nowMilliseconds = new Date().getTime();
  const deliveryStartMS = deliveryStart.getTime();

  let surcharge: IStage | undefined;

  surchargeStages.sort(sortStages).forEach((surchargeStage) => {
    const minDateOfSurchargeMS =
      deliveryStartMS - surchargeStage.daysBeforeBuild * oneDayInMS;
    if (minDateOfSurchargeMS <= nowMilliseconds) {
      surcharge = surchargeStage;
    }
  });

  if (!surcharge) {
    return 0;
  }

  if (surcharge?.type === SurchargeType.unpublish) {
    return SurchargeType.unpublish;
  }

  if (surcharge.type === SurchargeType.fixedFee) {
    return Number(surcharge.amount!);
  }

  const amount = (price / 100) * Number(surcharge.amount)!;
  return round(amount, 2);
};
export const calculateProductPrice = (
  deliveryStart: Date,
  stages: IStage[],
  productPrice?: number | null,
  showProductPrice?: number | null
) => {
  const price = showProductPrice || productPrice;
  if (!price) {
    return null;
  }
  if (!stages?.length) {
    return price;
  }
  const surchargeAmount = calculateSurchargeAmount(
    stages,
    price,
    deliveryStart
  );

  if (surchargeAmount === SurchargeType.unpublish) {
    return SurchargeType.unpublish;
  }

  const priceWithSurcharge = price + surchargeAmount;

  return round(priceWithSurcharge, 2);
};

export const calculateProductPriceWithFees = (
  price: number,
  organiserFee: number,
  supplierFee: number,
  dp = 2
) => {
  const calculatedPrice = price * ((organiserFee + supplierFee) / 100 + 1);
  return round(calculatedPrice, dp);
};
export const calculatePaymentFeeAmount = (price: number, fee: number) => {
  const calculatedPrice = price * (fee / 100);
  return round(calculatedPrice, 2);
};
export const calculatePlatformFeeAmount = (price: number, fee: number) =>
  round((price * fee) / 100, 2);

export const expandProductColors = (
  configs: ProductConfig[],
  fetchedColors: FetchedProductColour
) => {
  return configs.forEach((config) => {
    if (!config.items?.length) {
      return;
    }
    config.items.forEach(({ type, cpo, cpoItem }) => {
      if (type || !cpoItem || !isColorItem({ type, cpo })) {
        return;
      }
      if (!fetchedColors[cpoItem.id]) {
        fetchedColors[cpoItem.id] = {
          id: cpoItem.id,
          name: cpoItem.name,
          translationKey: cpoItem.translationKey,
        };
      }
    });
  });
};

export const mapExtras = (
  extras: IProductToExtra[],
  organiserCompany: IOrganiserCompany,
  organiserFee: number
): ExtraProduct[] =>
  extras
    .map(({ extra: extraProduct }) => {
      if (!extraProduct) return null;

      const price = getCpoPrice(extraProduct.customProductOptionConfigs ?? []);
      const quantity =
        getDefaultCpoConfigOrFirst(extraProduct.customProductOptionConfigs)
          ?.quantity ?? 0;

      const priceWithFees = calculateProductPriceWithFees(
        price,
        organiserCompany.fee,
        organiserFee
      );
      const paymentFeeAmount =
        organiserCompany?.paymentFee !== undefined
          ? calculatePaymentFeeAmount(price, organiserCompany.paymentFee)
          : null;
      const platformFeeAmount =
        organiserCompany?.fee !== undefined
          ? calculatePlatformFeeAmount(price, organiserCompany.fee)
          : null;

      return {
        id: extraProduct.id,
        name: extraProduct.name,
        isSelectedByDefault: extraProduct.isSelectedByDefault ?? false,
        quantity,
        price,
        priceWithFees,
        paymentFeeAmount,
        ...(platformFeeAmount && { platformFeeAmount }),
      } as ExtraProduct;
    })
    .filter(filterNotEmptyArray);

export const isProductAvailableQuantity = (
  configs: ProductConfig[]
): boolean => {
  const defaultConfig = configs.find(
    (c) => c.type === CustomProductOptionConfigType.DEFAULT && c.enabled
  );
  if (defaultConfig) {
    return isCpoConfigQuantityAvailable(defaultConfig);
  }

  return configs.some((c) => {
    if (c.type === CustomProductOptionConfigType.DEFAULT) {
      return false;
    }

    return isCpoConfigQuantityAvailable(c);
  });
};

type ExpandApiOptions = {
  platformFee?: number;
  organiserFee?: number;
  paymentFee?: number;
  files?: IProductFileWithFile[];
};

export const getCpoPrice = (configs: any[]): number =>
  parseCents(getDefaultCpoConfigOrFirst(configs)?.prices?.[0]?.price ?? 0);

export const expandApiProductFees = (
  product: IProduct,
  opts: ExpandApiOptions = {}
): Fee[] => {
  const { platformFee, organiserFee, paymentFee } = opts;

  if (!product?.productToFees?.length) return [];

  return product.productToFees
    .map((productFee) => {
      const { fee } = productFee;
      if (!fee) return null;

      const amount = getCpoPrice(fee.customProductOptionConfigs ?? []);

      const amountWithFees =
        platformFee !== undefined && organiserFee !== undefined
          ? calculateProductPriceWithFees(amount, platformFee, organiserFee)
          : amount;

      const platformFeeAmount =
        platformFee !== undefined
          ? calculatePlatformFeeAmount(amount, platformFee)
          : null;

      const paymentFeeAmount =
        paymentFee !== undefined
          ? calculatePaymentFeeAmount(amount, paymentFee)
          : null;

      return {
        ...fee,
        id: productFee.feeId,
        amount,
        amountWithFees,
        platformFeeAmount,
        paymentFeeAmount,
      } as Fee;
    })
    .filter(filterNotEmptyArray);
};

export const expandApiProductExtras = (product: IProduct): ExtraProduct[] => {
  if (!product?.productToExtras?.length) return [];

  return product.productToExtras
    .map((productExtra: any) => {
      const { extra } = productExtra;
      if (!extra) return null;

      const price = getCpoPrice(extra.customProductOptionConfigs ?? []);
      const quantity =
        getDefaultCpoConfigOrFirst(extra.customProductOptionConfigs)
          ?.quantity ?? 0;

      return {
        id: productExtra.extraId,
        name: extra.name,
        price,
        isSelectedByDefault: extra.isSelectedByDefault ?? false,
        quantity,
      } as ExtraProduct;
    })
    .filter(filterNotEmptyArray);
};

export const expandApiProduct = (
  product: IProduct,
  opts: ExpandApiOptions = {}
) => {
  const configs: ICustomProductOptionConfig[] =
    product.customProductOptionConfigs ?? [];
  const defaultConfig = getDefaultCpoConfigOrFirst(configs);

  const price = getCpoPrice(configs);
  const quantity: number = defaultConfig?.quantity ?? 0;
  const primaryImageId: string | null | undefined =
    defaultConfig?.primaryImageId;

  const productFiles = collectProductFiles(product);
  const image =
    (opts.files &&
      getSingleProductImage(opts.files, product.id, primaryImageId)) ||
    (primaryImageId
      ? productFiles.find(({ id }) => id === primaryImageId)
      : productFiles[0]);

  const fees = expandApiProductFees(product, opts);
  const extras = expandApiProductExtras(product);
  const recommendedProducts = (product.recommendedProducts ?? [])
    .map((rec) => rec.recommendedProduct)
    .filter((recommended): recommended is IProduct => !!recommended)
    .map(mapRecommendedProduct);

  const tagIds: string[] =
    product.productToTags?.map(({ tagId }) => tagId) ?? [];

  const showIds: string[] =
    product.productToShows?.map(({ showId }) => showId) ?? [];

  const priceWithFees =
    opts.platformFee !== undefined && opts.organiserFee !== undefined
      ? calculateProductPriceWithFees(
          price,
          opts.platformFee,
          opts.organiserFee
        )
      : price;

  return {
    ...product,
    price: price || null,
    priceWithFees,
    quantity,
    primaryImageId,
    ...(image && { image }),
    fees,
    extras,
    recommendedProducts,
    tagIds,
    showIds,
  };
};

export const mapRecommendedProduct = (product: IProduct): FullProduct => {
  const price = getCpoPrice(product.customProductOptionConfigs ?? []);
  const productFiles = collectProductFiles(product);
  const defaultConfig = getDefaultCpoConfigOrFirst(
    product.customProductOptionConfigs
  );
  const primaryImageId = defaultConfig?.primaryImageId;

  return {
    image:
      getSingleProductImage(productFiles, product.id, primaryImageId) ??
      productFiles[0],
    id: product.id,
    name: product.name,
    description: product.description,
    productId: product.productId,
    approval: product.approval,
    enablePriceOnApplication: product.enablePriceOnApplication,
    isExhibitorNotesRequired: product.isExhibitorNotesRequired,
    notesPlaceholder: product.notesPlaceholder ?? undefined,
    purchaseLimit: product.purchaseLimit ?? null,
    price: price || null,
    priceWithFees: price,
    quantity: defaultConfig?.quantity ?? 0,
    categories: product.categories ?? [],
    tagIds: product.productToTags?.map(({ tagId }) => tagId) ?? [],
    owner: product.supplierCompany,
    configs: (product.customProductOptionConfigs ?? []).map((config) => {
      const configPrice = getCpoPrice([config]);
      return { ...config, price: configPrice, priceWithFees: configPrice };
    }),
  } as FullProduct;
};
